/**
 * The Google Picker, loaded ON DEMAND and only in this one branch of one view.
 *
 * WHY THE PICKER AND NOT A TEXT FIELD. The platform client requests
 * `drive.file` and nothing else (api/src/googleOAuth.ts). That scope grants
 * access to files the app CREATED or the user PICKED through this dialog --
 * so a spreadsheet id typed into a form would be a sheet the server still
 * cannot read, and "Tabelle verbinden" would fail for a reason the operator
 * could not act on. The Picker is not a convenience here; it is the grant.
 *
 * SECOND AND LAST EXTERNAL SCRIPT in this SPA (components/Turnstile.tsx loads
 * the first, on the login view). Injected by this module, never bundled, and
 * never present on a page that has not asked for it.
 *
 * THE TOKEN. `POST /admin-api/google/picker-token` mints a short-lived,
 * drive.file-scoped access token server-side from the stored refresh token
 * (risk A6, admin spec 4.5): the refresh token never leaves the Worker. The
 * minted token is passed in as an argument, held for the duration of one
 * dialog, and never stored -- the storage ban in eslint.config.js is what
 * makes that mechanical rather than a promise.
 *
 * THE APP ID (Task 14 amendment B). Google recommends `setAppId` (the cloud
 * project number); this module now calls it WHEN `/admin-config.json` serves
 * one (worker/src/config.ts's `googlePickerAppId`). Until an owner
 * provisions a Google Cloud project it does not, and this module is never
 * asked to build a Picker at all in that case -- views/Sheet.tsx renders an
 * honest blocked panel instead, because under `drive.file` the Picker IS the
 * grant, there is deliberately no manual spreadsheet-id fallback, and the
 * plausible failure mode of opening one without the app id is SILENT.
 * `setDeveloperKey` remains unmodeled: it is documented as needed only "for
 * some deployments", and this one authenticates the Picker with an OAuth
 * token, which the same Google documentation calls sufficient on its own.
 */

const SCRIPT_SRC = 'https://apis.google.com/js/api.js';
const SCRIPT_ID = 'stackstock-google-api-script';

interface PickerDoc { id?: string }
interface PickerResponse { action?: string; docs?: PickerDoc[] }

interface PickerView { setIncludeFolders?: (on: boolean) => PickerView }
interface PickerBuilder {
  addView: (view: PickerView) => PickerBuilder;
  setOAuthToken: (token: string) => PickerBuilder;
  setAppId: (appId: string) => PickerBuilder;
  setCallback: (cb: (data: PickerResponse) => void) => PickerBuilder;
  setTitle: (title: string) => PickerBuilder;
  build: () => { setVisible: (visible: boolean) => void };
}

declare global {
  interface Window {
    gapi?: { load: (name: string, options: { callback: () => void; onerror?: () => void }) => void };
    google?: {
      picker?: {
        DocsView: new (viewId: string) => PickerView;
        ViewId: { SPREADSHEETS: string };
        PickerBuilder: new () => PickerBuilder;
        Action: { PICKED: string; CANCEL: string };
      };
    };
  }
}

/** Injects api.js once per document and resolves when `gapi.picker` is usable.
 *  Rejects rather than hanging: a blocked script must produce a named state,
 *  not a dialog that never opens. */
function loadPickerApi(): Promise<NonNullable<NonNullable<Window['google']>['picker']>> {
  return new Promise((resolve, reject) => {
    const ready = () => {
      const picker = window.google?.picker;
      if (picker) resolve(picker);
      else reject(new Error('google.picker did not load'));
    };
    if (window.google?.picker) {
      ready();
      return;
    }
    const loadModule = () => {
      if (!window.gapi) {
        reject(new Error('gapi did not load'));
        return;
      }
      window.gapi.load('picker', { callback: ready, onerror: () => reject(new Error('gapi.load(picker) failed')) });
    };
    if (window.gapi) {
      loadModule();
      return;
    }
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', loadModule, { once: true });
    script.addEventListener('error', () => reject(new Error('api.js failed to load')), { once: true });
  });
}

/**
 * Opens the dialog and resolves with the picked spreadsheet id, or `null` if
 * the operator closed it without choosing. Rejects only when the Picker could
 * not be loaded at all.
 *
 * `appId`, when given, is the Google Cloud project number from
 * `/admin-config.json`'s `googlePickerAppId` (Task 14 amendment B). Callers
 * that have none should not call this at all -- see the module docblock.
 */
export async function pickSpreadsheetId(accessToken: string, title: string, appId?: string): Promise<string | null> {
  const picker = await loadPickerApi();
  return new Promise((resolve) => {
    const view = new picker.DocsView(picker.ViewId.SPREADSHEETS);
    view.setIncludeFolders?.(false);
    const builder = new picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(accessToken)
      .setTitle(title);
    if (appId) builder.setAppId(appId);
    builder
      .setCallback((data) => {
        if (data.action === picker.Action.PICKED) {
          resolve(data.docs?.[0]?.id ?? null);
          return;
        }
        if (data.action === picker.Action.CANCEL) resolve(null);
        // Every other action ("loaded", "sizeChanged") is progress, not an
        // answer: resolving on those would close the dialog's promise while
        // the operator is still looking at it.
      })
      .build()
      .setVisible(true);
  });
}
